
import React, { useState } from 'react';
import { useCouriersData } from '@/hooks/useCouriersData';
import { Courier } from '@/services/types';
import { CourierFilters } from '@/components/couriers/CourierFilters';
import { CourierDetailsDialog } from '@/components/couriers/CourierDetailsDialog';
import { CouriersTable } from '@/components/couriers/CouriersTable';
import { CourierConnectionError } from '@/components/couriers/CourierConnectionError';
import { CourierStatsCard } from './couriers/components/CourierStatsCard';
import DiagnosticInfo from '@/components/supabase/DiagnosticInfo';
import { toast } from '@/components/ui/sonner';
import { filterCouriers } from '@/utils/courier/courierDataProcessing';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';

const CouriersPage: React.FC = () => {
  const { 
    couriers,
    loading,
    error, 
    connectionError,
    fetchCouriersData,
    deleteCourier,
    totalCount
  } = useCouriersData();
  
  const [selectedCourier, setSelectedCourier] = useState<Courier | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  
  const filteredCouriers = couriers.filter(courier => 
    statusFilter ? filterCouriers(courier, statusFilter) : true
  );

  const handleOpenCourierDetails = (courier: Courier) => {
    setSelectedCourier(courier);
    setShowDetailsDialog(true);
    console.log('Открываем детали курьера:', courier.id);
  };

  const handleDeleteCourier = async (courier: Courier) => {
    try {
      const success = await deleteCourier(courier.id);
      
      if (success) {
        if (selectedCourier && selectedCourier.id === courier.id) {
          setShowDetailsDialog(false);
          setSelectedCourier(null);
        }
      }
    } catch (e) {
      console.error('Ошибка при удалении курьера:', e);
      toast.error('Не удалось удалить курьера');
    }
  };

  const debugInfo = error || connectionError 
    ? { error, connectionError } 
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Управление клиентами</h1>
        <div className="flex items-center gap-2">
          <Button 
            onClick={fetchCouriersData}
            disabled={loading}
            variant="default"
            size="default"
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex-shrink-0"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>
      </div>

      <CourierFilters 
        setStatusFilter={setStatusFilter}
        couriers={filteredCouriers}
      />

      {connectionError && (
        <CourierConnectionError 
          debugInfo={debugInfo}
          onRetryConnection={() => fetchCouriersData()}
          onInitializeDatabase={() => console.log('Инициализация базы данных')}
        />
      )}

      {debugInfo && (
        <DiagnosticInfo diagnosticInfo={debugInfo} />
      )}

      <CourierStatsCard 
        couriers={couriers} 
        totalCount={totalCount}
        loading={loading}
      />

      <CouriersTable 
        couriers={filteredCouriers}
        loading={loading}
        onRowClick={handleOpenCourierDetails}
        onDeleteCourier={handleDeleteCourier}
      />

      {selectedCourier && (
        <CourierDetailsDialog 
          courier={selectedCourier}
          open={showDetailsDialog}
          onOpenChange={setShowDetailsDialog}
          onDeleteCourier={handleDeleteCourier}
        />
      )}
    </div>
  );
};

export default CouriersPage;
